/**
 * PDFViewer Component
 *
 * A simple PDF viewer using the browser's native PDF rendering via iframe.
 * More reliable than react-pdf and works with both blob URLs and remote URLs.
 *
 * @example
 * ```jsx
 * // With a URL
 * <PDFViewer url="https://example.com/document.pdf" />
 *
 * // With a File object (will create blob URL automatically)
 * <PDFViewer file={fileObject} />
 *
 * // With custom height
 * <PDFViewer url={pdfUrl} height="500px" />
 * ```
 */

import { useEffect, useState } from 'react';
import { DocumentIcon } from '@heroicons/react/24/outline';

const PDFViewer = ({
	url,
	file,
	title = 'PDF Document',
	height = '100%',
	className = '',
	showFallback = true,
	hideSidebar = true,
	hideToolbar = false,
}) => {
	const [pdfUrl, setPdfUrl] = useState(null);
	const [error, setError] = useState(false);

	// Create blob URL from File object if provided
	useEffect(() => {
		let baseUrl = null;

		if (file && file instanceof File) {
			baseUrl = URL.createObjectURL(file);
		} else if (url) {
			baseUrl = url;
		}

		if (baseUrl) {
			// Add PDF viewer parameters to hide sidebar and customize view
			const params = [];
			if (hideSidebar) params.push('navpanes=0');
			if (hideToolbar) params.push('toolbar=0');
			params.push('view=FitH'); // Fit to width

			const separator = baseUrl.includes('#') ? '&' : '#';
			setPdfUrl(
				params.length > 0
					? `${baseUrl}${separator}${params.join('&')}`
					: baseUrl,
			);
		}

		// Cleanup blob URL on unmount
		return () => {
			if (file && file instanceof File && baseUrl) {
				URL.revokeObjectURL(baseUrl);
			}
		};
	}, [file, url, hideSidebar, hideToolbar]);

	if (!pdfUrl) {
		return showFallback ? (
			<div className="flex flex-col items-center justify-center py-12 text-center bg-gray-50 rounded-lg border border-gray-200">
				<DocumentIcon className="w-12 h-12 text-gray-400 mb-3" />
				<p className="text-sm text-gray-500">No PDF to display</p>
			</div>
		) : null;
	}

	if (error) {
		return (
			<div className="flex flex-col items-center justify-center py-12 text-center bg-gray-50 rounded-lg border border-gray-200">
				<DocumentIcon className="w-12 h-12 text-gray-400 mb-3" />
				<p className="text-sm text-gray-600 mb-2">Unable to display PDF</p>
				<a
					href={pdfUrl}
					target="_blank"
					rel="noopener noreferrer"
					className="text-sm text-indigo-600 hover:text-indigo-800"
				>
					Open in new tab
				</a>
			</div>
		);
	}

	return (
		<iframe
			src={pdfUrl}
			title={title}
			className={`w-full rounded-lg border border-gray-200 bg-white ${className}`}
			style={{ height, backgroundColor: 'white' }}
			onError={() => setError(true)}
		/>
	);
};

export default PDFViewer;
